import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, Modal } from "@mui/material";
import { useEffect, useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { dark } from "react-syntax-highlighter/dist/esm/styles/prism";
import api, { RetailerDto, RetailerVendorLogDto, VendorDto } from "../../../api";
import { useUserContext } from "../../UserContext";
import { createDownloadLink, notif } from "../../utils";
import { RetailerVendorLogsGrid } from "./RetailerVendorLogsGrid";

export interface RetailerVendorLogsDialogAction {
  open: boolean;
  retailer?: RetailerDto;
  vendor?: VendorDto;
}

export interface RetailerVendorLogsDialogProps {
  action: RetailerVendorLogsDialogAction;
  onClose?: () => void;
}

export const RetailerVendorLogsDialog = (props: RetailerVendorLogsDialogProps) => {
  const { action, onClose } = props;

  const ctx = useUserContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [log, setLog] = useState("");
  const [fileName, setFileName] = useState("");
  const [downloadIsLoading, setDownloadIsLoading] = useState(false);
  const [screenshotFileName, setScreenshotFileName] = useState("");
  const [screenshotIsLoading, setScreenshotIsLoading] = useState(false);
  const [contentFileName, setContentFileName] = useState("");
  const [contentIsLoading, setContentIsLoading] = useState(false);
  const [imageContent, setImageContent] = useState("");

  const onLogSelected = (dto: RetailerVendorLogDto | null) => {
    let value = dto?.log ?? "";
    let file = "";
    let screenshot = "";
    let content = "";
    if (value && value.startsWith("{")) {
      try {
        const obj = JSON.parse(value);
        value = JSON.stringify(obj, null, 2);
        if (obj?.file?.name && typeof obj?.file?.name === "string") file = obj.file.name;
        if (obj?.screenshot && typeof obj?.screenshot === "string") screenshot = obj.screenshot;
        if (obj?.content && typeof obj?.content === "string") content = obj.content;
      } catch {
        // DO NOTHING
      }
    }
    setLog(value);
    setFileName(file);
    setScreenshotFileName(screenshot);
    setContentFileName(content);
  };

  const handleDownload = async () => {
    if (fileName) {
      setDownloadIsLoading(true);
      try {
        const file = await api.file.download(fileName);
        createDownloadLink(file, window);
      } catch (err) {
        notif(err, "error");
      }
      setDownloadIsLoading(false);
    }
  };
  const handleDownloadScreenshot = async () => {
    if (screenshotFileName) {
      setScreenshotIsLoading(true);
      try {
        const file = await api.file.download(screenshotFileName);
        // createDownloadLink(file, window);
        if (file.data) {
          var reader = new FileReader();
          reader.readAsDataURL(file.data);
          reader.onloadend = function () {
            setImageContent(reader.result as string);
          };
        }
      } catch (err) {
        notif(err, "error");
      }
      setScreenshotIsLoading(false);
    }
  };
  const handleDownloadContent = async () => {
    if (contentFileName) {
      setContentIsLoading(true);
      try {
        const file = await api.file.download(contentFileName);
        createDownloadLink(file, window);
      } catch (err) {
        notif(err, "error");
      }
      setContentIsLoading(false);
    }
  };

  const handleCloseImage = () => {
    setImageContent("");
  }

  const handleClose = () => {
    if (onClose) onClose();
    setDialogOpen(false);
  };

  useEffect(() => {
    if (action.open && action.retailer?.id && action.vendor?.id) {
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
    }
  }, [action]);

  return (
    <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xl">
      <DialogTitle>
        RETAILER '{action?.retailer?.name}' VENDOR '{action?.vendor?.name}' LOGS
      </DialogTitle>
      <DialogContent dividers>
        <FormGroup>
          <RetailerVendorLogsGrid retailerId={action?.retailer?.id || ""} vendorId={action?.vendor?.id || ""} onRowSelected={onLogSelected} />
          <Box sx={{ p: 1 }} />
          {log && (
            <Box>
              <SyntaxHighlighter language="json" style={ctx.themeName.indexOf("dark") >= 0 ? dark : docco}>
                {log}
              </SyntaxHighlighter>
            </Box>
          )}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <LoadingButton disabled={!fileName} endIcon={<SaveAltOutlinedIcon />} loadingPosition="end" loading={downloadIsLoading} onClick={handleDownload}>
          Download file
        </LoadingButton>
        <LoadingButton disabled={!screenshotFileName} endIcon={<SaveAltOutlinedIcon />} loadingPosition="end" loading={screenshotIsLoading} onClick={handleDownloadScreenshot}>
          Screenshot
        </LoadingButton>
        <LoadingButton disabled={!contentFileName} endIcon={<SaveAltOutlinedIcon />} loadingPosition="end" loading={contentIsLoading} onClick={handleDownloadContent}>
          Content
        </LoadingButton>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
      {imageContent && (<Modal
        open
        onClose={handleCloseImage}
        closeAfterTransition>
        <img src={imageContent} style={{ maxHeight: "90%", maxWidth: "90%" }} />
      </Modal>)}
    </Dialog>
  );
};
